import { useState } from "react";
import {
  Drawer,
  Button,
  Table,
  message,
  Spin,
  Alert,
  Space,
  Tag,
  Popconfirm,
  Form,
  Input,
  Typography,
} from "antd";
import {
  ReloadOutlined,
  CheckCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { drawerTitle, drawerFooter, DRAWER_FORM_WIDTH } from "../../../../components";
import {
  useSystemConfigListQuery,
  useCreateSystemConfigMutation,
  useUpdateSystemConfigMutation,
  useDeleteSystemConfigMutation,
} from "../../../../Services";
import { ConfigSectionHeader } from "../config/ConfigSectionHeader";

const { Text } = Typography;

interface ConfigItem {
  id: string;
  config_name: string;
  value: string;
  created_at: string;
  updated_at: string;
}

interface Props {
  open: boolean;
  tenantId: string;
  onClose: () => void;
}

export const TenantConfigDrawer: React.FC<Props> = ({ open, tenantId, onClose }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingItem, setEditingItem] = useState<ConfigItem | null>(null);
  const [form] = Form.useForm();

  const { data: configs = [], isLoading, isError, isFetching, error, refetch } = useSystemConfigListQuery(tenantId, { enabled: open });

  const createMutation = useCreateSystemConfigMutation();
  const updateMutation = useUpdateSystemConfigMutation();
  const deleteMutation = useDeleteSystemConfigMutation();

  const mutationPending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const loading = isFetching || mutationPending;

  const handleCreate = () => {
    setEditingItem(null);
    setIsEditing(false);
    setIsCreating(true);
    form.resetFields();
  };

  const handleEdit = (item: ConfigItem) => {
    setIsCreating(false);
    setIsEditing(true);
    setEditingItem(item);
    form.setFieldsValue({
      config_name: item.config_name,
      value: item.value,
    });
  };

  const handleCancelForm = () => {
    setIsCreating(false);
    setIsEditing(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSave = async (values: any) => {
    try {
      if (isCreating) {
        const res = await createMutation.mutateAsync({
          config_name: values.config_name,
          value: values.value,
          tenant_id: tenantId,
        });
        if (res?.success) {
          message.success("Configuration created successfully.");
          handleCancelForm();
        } else {
          throw new Error(res?.message || "Failed to create configuration.");
        }
      } else if (isEditing && editingItem) {
        const res = await updateMutation.mutateAsync({
          id: editingItem.id,
          config_name: values.config_name,
          value: values.value,
          tenant_id: tenantId,
        });
        if (res?.success) {
          message.success("Configuration updated successfully.");
          handleCancelForm();
        } else {
          throw new Error(res?.message || "Failed to update configuration.");
        }
      }
    } catch (err: any) {
      message.error(err.message || "Error saving configuration.");
    }
  };

  const handleDelete = async (item: ConfigItem) => {
    try {
      const res = await deleteMutation.mutateAsync(item.id);
      if (res?.success) {
        message.success("Configuration deleted successfully.");
      } else {
        throw new Error(res?.message || "Failed to delete configuration.");
      }
    } catch (err: any) {
      message.error(err.message || "Error deleting configuration.");
    }
  };

  const columns = [
    {
      title: "Config Name",
      dataIndex: "config_name",
      key: "config_name",
      render: (text: string) => (
        <Text code className="text-xs font-semibold">{text}</Text>
      ),
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      ellipsis: true,
      render: (text: string) => (
        <Text className="text-xs text-gray-600">{text || "-"}</Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, item: ConfigItem) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined className="text-blue-500" />}
            size="small"
            onClick={() => handleEdit(item)}
          />
          <Popconfirm
            title="Are you sure you want to delete this configuration?"
            onConfirm={() => handleDelete(item)}
            okText="Yes"
            cancelText="No"
            placement="left"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const errorMsg = isError ? (error as any)?.message || "Failed to load configurations." : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={DRAWER_FORM_WIDTH}
      title={drawerTitle("System Configuration", "Manage system-level config key-value pairs")}
      destroyOnClose
      footer={drawerFooter({ actions: [{ label: "Close", onClick: onClose }] })}
    >
      <div className="flex flex-col gap-4 h-full">
        {errorMsg && (
          <Alert
            message="Connection Error"
            description={
              <div className="flex flex-col gap-2">
                <p className="text-xs text-red-800">{errorMsg}</p>
                <Button size="small" type="primary" danger icon={<ReloadOutlined />} onClick={() => refetch()} className="w-fit text-xs">
                  Retry Connection
                </Button>
              </div>
            }
            type="error"
            showIcon
          />
        )}

        {!errorMsg && !isLoading && (
          <Alert
            message={
              <div className="flex items-center justify-between">
                <Space>
                  <CheckCircleOutlined className="text-green-500" />
                  <span className="text-xs font-semibold text-green-800">
                    Connected &mdash; {configs.length} configuration(s)
                  </span>
                </Space>
                <Button size="small" icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching && !isCreating && !isEditing} className="text-[11px] h-6">
                  Refresh
                </Button>
              </div>
            }
            type="success"
            showIcon={false}
            className="!border-green-100 !bg-green-50"
          />
        )}

        {!isCreating && !isEditing && (
          <ConfigSectionHeader
            description="Add or modify system configuration values."
            onAdd={handleCreate}
          />
        )}

        {(isCreating || isEditing) && (
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <SettingOutlined className="text-slate-500" />
                {isCreating ? "New Configuration" : "Edit Configuration"}
              </span>
              <Button type="text" size="small" shape="circle" icon={<CloseOutlined className="text-xs" />} onClick={handleCancelForm} />
            </div>

            <Form form={form} layout="vertical" onFinish={handleSave} size="middle">
              <Form.Item name="config_name" label="Config Name" rules={[{ required: true, message: "Config name is required." }]}>
                <Input placeholder="e.g. company_name" className="text-xs" disabled={isEditing} />
              </Form.Item>

              <Form.Item name="value" label="Value" rules={[{ required: true, message: "Value is required." }]}>
                <Input placeholder="e.g. Acme Corp" className="text-xs" />
              </Form.Item>

              <Form.Item className="mb-0">
                <Space className="w-full justify-end">
                  <Button size="small" onClick={handleCancelForm}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={createMutation.isPending || updateMutation.isPending} size="small" className="!bg-black !border-black hover:!bg-gray-800">
                    Save Changes
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}

        <div className="flex-grow overflow-auto relative min-h-[200px]">
          {isLoading && (
            <div className="flex items-center justify-center h-40">
              <Spin tip="Loading configurations..." size="large" />
            </div>
          )}

          {configs.length > 0 && (
            <Table dataSource={configs} columns={columns} rowKey="id" size="small" pagination={false} className="border rounded-lg" locale={{ emptyText: "No configurations found." }} />
          )}

          {configs.length === 0 && !isLoading && !isCreating && !isEditing && (
            <div className="text-center text-gray-400 text-sm py-10">
              No configurations found. Click "Add Config" to create one.
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};
